// Added by Sejune Oh on 2019-06-26
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { Component, createRef, RefObject } from 'react'
import { inject, observer } from 'mobx-react'
import validator from 'validator'
import moment from 'moment'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { InputValidator, InputBlurValidator } from '@/helper/define'
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { UserStore } from '@/stores/mgmt/user'
import { UserBaseCProps } from './UserBase'
import { SiteConfigStore } from '@/stores/mgmt/config'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface UserValidatorCProps extends UserBaseCProps {
    siteConfig?: SiteConfigStore
    user?: UserStore
    forwardedRef?: RefObject<any>
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const WithUserValidator = () => (WrappedComponent: any) => {
    @inject('siteConfig', 'user')
    @observer
    class WithUserValidator extends Component<UserValidatorCProps> {
        _userDescLengthCondition = { max: 256 }
        _userNameLengthCondition = { min: 2, max: 32 }
        _userDisplayNameLengthCondition = { min: 1, max: 32 }

        _pwdRef = createRef<any>()
        _confirmPwdRef = createRef<any>()
        _emailRef = createRef<any>()
        _usernameRef = createRef<any>()

        render() {
            const validator = {
                validator: {
                    userNameValidator: this._userNameValidator,
                    emailValidator: this._emailValidator,
                    displayNameValidator: this._displayNameValidator,
                    pwdValidator: this._pwdValidator,
                    descValidator: this._descValidator,
                    confirmPwdValidator: this._confirmPwdValidator,
                    curPwdValidator: this._curPwdValidator,
                    dateValidator: this._dateValidator
                },
                blurValidator: {
                    userNameBlurValidator: this._userNameBlurValidator,
                    emailBlurValidator: this._emailBlurValidator,
                    pwdBlurValidator: this._pwdBlurValidator,
                    confirmPwdBlurValidator: this._confirmPwdBlurValidator,
                    dateBlurValidator: this._dateBlurValidator
                },
                pwdRef: this._pwdRef,
                confirmPwdRef: this._confirmPwdRef,
                emailRef: this._emailRef,
                usernameRef: this._usernameRef,
            }

            return <WrappedComponent ref={this.props.forwardedRef} {...this.props} {...validator} />
        }

        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        // Validator
        _userNameValidator: InputValidator = (value) => {
            if (!validator.isByteLength(value as string, this._userNameLengthCondition)) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_name_length' })
                return [true, eMessage]
            }

            if (!validator.matches(value as string, new RegExp('^[a-zA-Z0-9._]*$'))) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_invalid_format' })
                return [true, eMessage]
            }

            return [false]
        }

        _emailValidator: InputValidator = (value) => {
            if (!validator.isEmail(value as string)) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_invalid_email_format' })
                return [true, eMessage]
            }
            return [false]
        }

        _displayNameValidator: InputValidator = (value) => {
            if (!validator.isByteLength(value as string, this._userDisplayNameLengthCondition)) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_disp_name_length' })
                return [true, eMessage]
            }

            return [false]
        }

        _pwdValidator: InputValidator = (value) => {
            // Config Options
            const { siteConfig } = this.props
            const pwdMustIncludeNumber = siteConfig!.GetSiteConfigByKey('PasswordRule.MustIncludeNumber') as string
            const pwdMustIncludeSymbol = siteConfig!.GetSiteConfigByKey('PasswordRule.MustIncludeSymbol') as string
            const pwdMustNotIncludeID = siteConfig!.GetSiteConfigByKey('PasswordRule.MustNotIncludeID') as string
            const pwdMixedCase = siteConfig!.GetSiteConfigByKey('PasswordRule.MustIncludeMixedCase') as string
            const pwdMinLengthValue = siteConfig!.GetSiteConfigByKey('PasswordRule.MinLength') as string
            const pwdMinLength = parseInt(pwdMinLengthValue)

            // RegEXP
            const mixedCaseRegex = new RegExp('(?=.*?[a-z])(?=.*?[A-Z])[0-9a-zA-Z!@#$%^&*]{1,}')
            const includedNumRegex = new RegExp('(?=.*?[0-9])[0-9a-zA-Z!@#$%^&*]{1,}')
            const includedSymbolRegex = new RegExp(
                '(?=.*?[!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~])[0-9a-zA-Z!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~]{1,}'
            )
            let eMessage = ''

            if (pwdMinLength > -1 && !validator.isLength(value as string, { min: pwdMinLength })) {
                // prettier-ignore
                eMessage = Intl.getIntlTextWithOption({ id: 'htmljs_react.mgmt.uservalidator.msg_pwd_min_length' }, { min: pwdMinLength })
                return [true, eMessage]
            }

            if (pwdMixedCase === 'true' && !validator.matches(value as string, mixedCaseRegex)) {
                eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_pwd_mixed_case' })
                return [true, eMessage]
            }

            if (pwdMustIncludeNumber === 'true' && !validator.matches(value as string, includedNumRegex)) {
                eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_pwd_included_num' })
                return [true, eMessage]
            }

            if (pwdMustIncludeSymbol === 'true' && !validator.matches(value as string, includedSymbolRegex)) {
                eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_pwd_included_symbol' })
                return [true, eMessage]
            }

            if (pwdMustNotIncludeID === 'true') {
                const curId = this._usernameRef.current!.value as string
                const curEmail = this._emailRef.current!.value as string
                const emailId = curEmail.split('@')[0]
                const msg = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_pwd_included_id' })

                if (emailId !== '' && validator.contains(value as string, emailId)) {
                    return [true, msg]
                }

                if (curId !== '' && validator.contains(value as string, curId)) {
                    return [true, msg]
                }
            }

            // Call Confirm PWD Validator
            if (this._confirmPwdRef.current) this._confirmPwdRef.current!.callValidator()

            return [false]
        }

        _curPwdValidator: InputValidator = (value) => {
            if (validator.isEmpty(value as string, { ignore_whitespace: true })) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_not_empty' })
                return [true, eMessage]
            }

            return [false]
        }

        _confirmPwdValidator: InputValidator = (value) => {
            const curPwd = this._pwdRef.current!.value

            if (!value) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_not_empty' })
                return [true, eMessage]
            }

            if (curPwd !== value) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_pwd_not_matched' })
                return [true, eMessage]
            }

            return [false]
        }

        _descValidator: InputValidator = (value) => {
            if (!validator.isByteLength(value as string, this._userDescLengthCondition)) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_desc_length' })
                return [true, eMessage]
            }

            return [false]
        }

        _dateValidator: InputValidator = (value) => {
            if (value === '0') return [false]
            if (!moment(value, 'YYYYMMDD', true).isValid()) {
                const msgInvalidDate = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_invalid_date' })
                return [true, msgInvalidDate]
            }
            return [false]
        }

        //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
        // Blur Validator
        _userNameBlurValidator: InputBlurValidator = async (value) => {
            if (await this.props.user!.checkDuplicatedUserName(value as string)) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_duplicated_name' })
                return [true, eMessage]
            }

            return [false]
        }

        _emailBlurValidator: InputBlurValidator = async (value) => {
            if (await this.props.user!.checkDuplicatedEmail(value as string)) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_duplicated_email' })
                return [true, eMessage]
            }

            return [false]
        }

        _pwdBlurValidator: InputBlurValidator = async (value) => {
            if (this._confirmPwdRef.current) this._confirmPwdRef.current!.callValidator()
            return [false]
        }

        _confirmPwdBlurValidator: InputBlurValidator = async (value) => {
            const curPwd = this._pwdRef!.current!.value
            const curConfirmPwd = this._confirmPwdRef!.current!.value

            if (!value) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_not_empty' })
                return [true, eMessage]
            }

            if (curPwd !== curConfirmPwd) {
                const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_pwd_not_matched' })
                return [true, eMessage]
            }

            return [false]
        }

        _dateBlurValidator: InputBlurValidator = async (value) => {
            if (value === '0') return [false]
            if (!moment(value as string, 'YYYYMMDD', true).isValid()) {
                const msgInvalidDate = Intl.getIntlText({ id: 'htmljs_react.mgmt.uservalidator.msg_invalid_date' })
                return [true, msgInvalidDate]
            }
            return [false]
        }
    }

    return React.forwardRef<any, any>((props, ref) => {
        return <WithUserValidator {...props} forwardedRef={ref} />
    })
}

export default WithUserValidator
